import { Component, ChangeDetectionStrategy, computed } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";
import { TranslateModule } from "@ngx-translate/core";

interface UserProfile {
  homeAddress?: {
    city?: string;
  };
}

/**
 * Custom Formly field type for radius selection dropdown.
 * Supports optional "from profile" location display.
 */
@Component({
  selector: "formly-radius-select",
  imports: [ReactiveFormsModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="mb-3">
      @if (props.label) {
        <h5 class="font-alternate text-sm font-semibold text-black tracking-tight mb-1.5">
          {{ props.label | translate }}
        </h5>
      }
      @if (props.showFromProfile && userProfile()) {
        <p class="text-xs text-gray-400 mb-1">
          {{ "From location" | translate }}: {{ userProfile()?.homeAddress?.city || '' }}
        </p>
      }
      <div class="flex rounded border border-gray-200 overflow-hidden focus-within:ring-1 focus-within:ring-mty-primary focus-within:border-mty-primary transition-shadow">
        <span class="inline-flex items-center px-2.5 text-gray-400 bg-gray-50 border-r border-gray-200">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
          </svg>
        </span>
        <select
          class="flex-1 min-w-0 px-3 py-1.5 text-sm border-0 outline-none bg-white cursor-pointer appearance-none"
          [value]="formControl.value || ''"
          (change)="onChange($any($event.target).value)"
        >
          <option value="">{{ "Select distance" | translate }}</option>
          @for (option of props.options || []; track option.value) {
            <option [value]="option.value">{{ option.label }}</option>
          }
        </select>
        <span class="inline-flex items-center px-2 text-gray-400 bg-white pointer-events-none">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </span>
      </div>
    </div>
  `,
})
export class RadiusSelectFieldType extends FieldType<FieldTypeConfig> {
  userProfile = computed<UserProfile | null>(() => {
    if (this.props.userProfileResolver) {
      return this.props.userProfileResolver();
    }
    return null;
  });

  onChange(value: string): void {
    this.formControl.setValue(value);
  }
}
